import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { useEffect, useMemo } from 'react'
import createInMemoryCache from './cache'
import { withScalars } from 'apollo-link-scalars'
import { buildClientSchema, IntrospectionQuery } from 'graphql'
import { DateTimeResolver } from 'graphql-scalars'
import graphQLSchema from './graphql.schema.json'
import { useAuth } from '../../app/hooks/auth/auth.hook'
import { HicDateResolver } from './hic-date-resolver'
import { HicTimeResolver } from './hic-time-resolver'
import { getMainDefinition } from '@apollo/client/utilities'
import { refreshToken } from '@ts-digital/digital-login-component'
import jwt from 'jwt-decode'
import { TokenLoginInfo } from 'src/app/types/login.types'

const useApolloClient = () => {
  const { auth, logout, refresh } = useAuth()

  const digitalEnv = useMemo(() => {
    switch (process.env.REACT_APP_ENV) {
      case 'prod':
        return 'prod'
      case 'test':
      case 'stage':
        return 'test'
      case 'dev':
        return 'dev'
      default:
        return undefined
    }
  }, [process.env.REACT_APP_ENV])

  useEffect(() => {
    if (auth.isAuthenticated) {
      const { exp } = jwt<TokenLoginInfo>(auth.token)
      const tokenExpiration = exp * 1000
      const now = +new Date()
      const tollerance = 15 * 60 * 1000 //15 min
      const timer = tokenExpiration - now - tollerance
      console.log(timer)
      const interval = setInterval(async () => {
        try {
          const refreshResponse = await refreshToken(auth.refreshToken, process.env.REACT_APP_ONEPLATFORM_APPCODE_HOTELINCLOUD || 'TS320', digitalEnv)
          refresh(refreshResponse.accessToken, refreshResponse.refreshToken)
        } catch (e) {
          logout()
        }
      }, timer) // calculate timer using JWT residual duration - 15 min
      return () => clearInterval(interval)
    }
  }, [auth, digitalEnv])

  const client = useMemo(() => {
    const cache = createInMemoryCache()

    let linkChain = withScalars({
      schema: buildClientSchema(graphQLSchema as unknown as IntrospectionQuery),
      typesMap: {
        Date: HicDateResolver,
        DateTime: DateTimeResolver,
        Time: HicTimeResolver,
      },
    })

    const authLink = setContext((_, { headers }) => {
      const token = localStorage.getItem('hic-token')
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${token ? token : process.env.REACT_APP_PUBLIC_TOKEN}`,
        },
      }
    })

    linkChain = linkChain.concat(authLink)

    // linkChain = linkChain.concat(
    //   new ApolloLink((operation, forward) => {
    //     const headers: { [key: string]: string } = {}
    //     if (auth?.isAuthenticated) {
    //       headers.Authorization = `Bearer ${auth.token}`
    //     } else {
    //       headers.Authorization = `Bearer ${process.env.REACT_APP_PUBLIC_TOKEN}`
    //     }
    //     operation.setContext({ headers })
    //     return forward(operation)
    //   }),
    // )

    const cleanTypeName = new ApolloLink((operation, forward) => {
      if (operation.variables) {
        const omitTypename = (key: string, value: any) => (key === '__typename' ? undefined : value)
        operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename)
      }
      return forward(operation).map((data) => {
        return data
      })
    })
    linkChain = linkChain.concat(cleanTypeName)

    linkChain = linkChain.concat(
      onError(({ graphQLErrors }) => {
        if (graphQLErrors) {
          console.log(graphQLErrors)
          const unauthenticatedError = graphQLErrors.find(({ extensions }) => extensions && extensions.code && extensions.code === GraphQLErrorCode.INVALID_JWT_TOKEN.toString())
          console.log(unauthenticatedError)
          if (unauthenticatedError) {
            logout()
            // if (!auth.loading && auth.isAuthenticated) {
            //   // try the refresh
            //   refreshToken(auth.refreshToken, process.env.REACT_APP_ONEPLATFORM_APPCODE_HOTELINCLOUD || 'TS320', process.env.NODE_ENV === 'production' ? 'prod' : 'dev')
            //     .then((refreshResponse) => {
            //       refresh(refreshResponse.accessToken, refreshResponse.refreshToken)
            //       window.location.reload()
            //       // TODO gestione temporanea con refresh della pagina per forzare la nuova esecuzione di tutte le query con il nuovo token appena refreshato
            //     })
            //     .catch(() => {
            //       // TODO andrebbe fatto un logout, ma al momento evitiamo finchè non c'è una gestione corretta del refresh
            //     })
            // }
          } else {
            const userNotConfiguratedError = graphQLErrors.find(({ extensions }) => extensions && extensions.code && extensions.code === GraphQLErrorCode.USER_NOT_CONFIGURED)

            if (userNotConfiguratedError) {
              window.location.replace('/missing_user_role')
              logout()
            }
            const userNotPresentError = graphQLErrors.find(({ extensions }) => extensions && extensions.code && extensions.code === GraphQLErrorCode.USER_NOT_PRESENT)
            if (userNotPresentError) {
              window.location.replace('/missing_user_role')
              // window.location.replace(process.env.REACT_APP_MARKETPLACE_URL || 'https://www.teamsystem.com')
              logout()
            }
          }
        }
      }),
    )

    if (process.env.REACT_APP_WS_URL) {
      linkChain = linkChain.split(
        ({ query }) => {
          // every operation of type "subscription" it's sent with WebSockets
          const definition = getMainDefinition(query)
          return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
        },
        new GraphQLWsLink(
          createClient({
            url: process.env.REACT_APP_WS_URL,
          }),
        ),
        createHttpLink({
          uri: process.env.REACT_APP_API_URL,
        }),
      )
    } else {
      linkChain = linkChain.concat(
        createHttpLink({
          uri: process.env.REACT_APP_API_URL,
        }),
      )
    }

    return new ApolloClient({
      cache: cache,
      link: linkChain,
      connectToDevTools: true,

      // COMMENTATO => IMPOSTAZIONI DI DEFAULT
      defaultOptions: {
        query: {
          fetchPolicy: 'network-only',
        },
        watchQuery: {
          fetchPolicy: 'network-only',
          nextFetchPolicy: 'cache-first',
        },
      },
    })
  }, [])

  return client
}

export default useApolloClient

enum GraphQLErrorCode {
  INVALID_JWT_TOKEN = 'INVALID_JWT_TOKEN',
  USER_NOT_CONFIGURED = 'USER_NOT_CONFIGURED',
  USER_NOT_PRESENT = 'USER_NOT_PRESENT',
  GENERIC = 'GENERIC',
}
